import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Body1, Body5, Body7, Container } from '@ui';
import { FlatButton, SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { DOWNLOAD_KINDS, isRunFinished } from '@services/fmdc';
import { useRun } from './useRun';
import { useRunDownload } from './useRunDownload';
import { useUrlFilters } from '@hooks/application/useUrlFilters';
import { fmdcPaths, runDetailUrl, RUN_TABS } from './fmdcUrl';
import { RunStatusBadge } from './RunStatusBadge';
import { StageStepper } from './StageStepper';
import { RunOverview } from './RunOverview';
import { RunValidation } from './RunValidation';
import { RunStaticInfo } from './RunStaticInfo';
import { RerunConfirm } from './RerunConfirm';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { formatCount, formatDateTime, formatRelative } from './format';

// The run's home: status, counts, the validation report, Static_Info, downloads and rerun.
// Three long waits live here — ingestion, the workbook render, the Static_Info answers — and
// each has its own copy and an elapsed indicator, because a demo that looks hung is a failed demo.

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

const Recap = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const TABS = [
  { key: RUN_TABS.OVERVIEW, label: 'Overview' },
  { key: RUN_TABS.VALIDATION, label: 'Validation' },
  { key: RUN_TABS.STATIC_INFO, label: 'Static Info' },
];

// One-line recap of the run, echoing only the fields present (the BackfillProgress shape).
export const buildRecap = (run) => {
  if (!run) return null;
  const parts = [];
  if (run.issuer_id)
    parts.push(run.issuer_name ? `Issuer ${run.issuer_id} (${run.issuer_name})` : `Issuer ${run.issuer_id}`);
  if (run.template_version) parts.push(run.template_version);
  if (run.counts?.model_cells != null) parts.push(`${formatCount(run.counts.model_cells)} cells`);
  return parts.length ? parts.join(' · ') : null;
};

export const RunDetailView = ({ runId }) => {
  const navigate = useNavigate();
  const { run, isLoading, error, reload, setRun } = useRun(runId);
  const download = useRunDownload(runId);
  const { filters, setFilters } = useUrlFilters(runDetailUrl);
  const [confirmingRerun, setConfirmingRerun] = React.useState(false);

  if (isLoading && !run) {
    return <Body5 isLowContrast>Loading run…</Body5>;
  }

  if (!run) {
    return (
      <div>
        <Body5>{error}</Body5>
        <Container top={12}>
          <SkeletonButton onClick={reload}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  const finished = isRunFinished(run);
  const openCount = run.review?.open ?? 0;
  const canRerun = run.rerunnable === true || run.stuck === true;
  const recap = buildRecap(run);

  const startDownload = (kind) => {
    if (!finished || download.isRunning) return;
    download.download(kind);
  };

  const downloadLabel = (kind, idle) => (download.isRunning && download.kind === kind ? 'Downloading…' : idle);

  const renderTab = () => {
    if (filters.tab === RUN_TABS.VALIDATION) return <RunValidation runId={runId} />;
    if (filters.tab === RUN_TABS.STATIC_INFO) return <RunStaticInfo runId={runId} />;
    return <RunOverview run={run} />;
  };

  return (
    <div>
      <HeaderRow>
        <div>
          <Body1 bold>{run.source_name || `Run #${run.id}`}</Body1>
          <Container top={8}>
            <Recap>
              <RunStatusBadge run={run} />
              {recap && <Body7>{recap}</Body7>}
              <Body7 title={formatDateTime(run.created_at)}>{`Uploaded ${formatRelative(run.created_at)}`}</Body7>
            </Recap>
          </Container>
        </div>

        <div>
          <Actions>
            {finished && (
              <FlatButton onClick={() => navigate(fmdcPaths.review(ROUTES.fmdc, run.id))}>
                {`Open review queue (${formatCount(openCount)} open)`}
              </FlatButton>
            )}
            <SkeletonButton
              onClick={() => startDownload(DOWNLOAD_KINDS.WORKBOOK)}
              disabled={!finished || download.isRunning}
              buttonProps={{ 'aria-disabled': !finished || download.isRunning }}
            >
              {downloadLabel(DOWNLOAD_KINDS.WORKBOOK, 'Download workbook')}
            </SkeletonButton>
            <SkeletonButton
              onClick={() => startDownload(DOWNLOAD_KINDS.PAYLOAD)}
              disabled={!finished || download.isRunning}
              buttonProps={{ 'aria-disabled': !finished || download.isRunning }}
            >
              {downloadLabel(DOWNLOAD_KINDS.PAYLOAD, 'Download JSON')}
            </SkeletonButton>
            {canRerun && <SkeletonButton onClick={() => setConfirmingRerun(true)}>Rerun</SkeletonButton>}
          </Actions>
          {!finished && (
            <Container top={6} row alignRight>
              <Body7>Downloads are available when the run finishes.</Body7>
            </Container>
          )}
          {download.showElapsedHint && (
            <Container top={6} row alignRight>
              <Body7 isLowContrast role="status">
                {`Still working… ${download.elapsedSeconds}s. The file is rendered on demand and can take a minute.`}
              </Body7>
            </Container>
          )}
          {download.error && (
            <Container top={6} row alignRight>
              <NoticeText tone={NOTICE_TONES.ERROR}>{download.error}</NoticeText>
            </Container>
          )}
        </div>
      </HeaderRow>

      {error && (
        <Container top={12}>
          <NoticeText tone={NOTICE_TONES.ERROR}>{error}</NoticeText>
        </Container>
      )}

      <Container top={24}>
        <StageStepper run={run} />
      </Container>

      <Container top={24}>
        <SegmentedControl
          label="Section"
          options={TABS}
          active={filters.tab}
          onSelect={(tab) => setFilters({ tab })}
          segmentWidth={110}
        />
      </Container>

      <Container top={24}>{renderTab()}</Container>

      {confirmingRerun && (
        <RerunConfirm
          run={run}
          onDone={(next) => {
            setConfirmingRerun(false);
            if (next) setRun(next);
            else reload();
          }}
          onCancel={() => setConfirmingRerun(false)}
        />
      )}
    </div>
  );
};
